import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { getPlatformColor } from "../PlatformBadge";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

interface ConversionFunnelChartProps {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function ConversionFunnelChart({ isEditMode, onRemove, onResize, onResetSize }: ConversionFunnelChartProps) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["unified-funnel", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("platform-breakdown", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json() as Promise<Array<{
        platformSlug: string;
        platformName: string;
        impressions: number;
        clicks: number;
        conversions: number;
        spend: number;
      }>>;
    },
    enabled: sourceType === "unified",
  });

  const platformSlugsFound = data?.map(d => d.platformSlug) || [];
  const funnelStages = ["Impressions", "Clicks", "Conversions"];

  const chartData = funnelStages.map(stage => {
    const row: Record<string, string | number> = { stage };
    data?.forEach(p => {
      if (stage === "Impressions") row[p.platformSlug] = p.impressions;
      else if (stage === "Clicks") row[p.platformSlug] = p.clicks;
      else if (stage === "Conversions") row[p.platformSlug] = p.conversions;
    });
    return row;
  });

  return (
    <DashboardWidget
      title="Conversion Funnel"
      widgetType="chart"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="unified"
    >
      <div className="p-4 h-full flex flex-col" data-testid="chart-conversion-funnel">
        {!data || data.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "#5F6D82" }}>
            No funnel data
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: 10, right: 20 }}>
                  <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {platformSlugsFound.map(slug => (
                    <Bar
                      key={slug}
                      dataKey={slug}
                      stackId="funnel"
                      fill={getPlatformColor(slug)}
                      radius={[0, 0, 0, 0]}
                      name={data?.find(d => d.platformSlug === slug)?.platformName || slug}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              {data && data.length > 0 && (() => {
                const totalImp = data.reduce((s, p) => s + p.impressions, 0);
                const totalClicks = data.reduce((s, p) => s + p.clicks, 0);
                const totalConv = data.reduce((s, p) => s + p.conversions, 0);
                return (
                  <>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400">Click Rate</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {totalImp > 0 ? ((totalClicks / totalImp) * 100).toFixed(2) : 0}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-gray-400">Conv. Rate</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {totalClicks > 0 ? ((totalConv / totalClicks) * 100).toFixed(2) : 0}%
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </DashboardWidget>
  );
}
